import { EventEmitter } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { HelpText } from '../../model/help-text';
import { ValidationErrorAtLeastOne } from './form-group.validators';

export const validationErrorCode = Object.freeze({
  email: 'email',
  max: 'max',
  maxLength: 'maxlength',
  min: 'min',
  minLength: 'minlength',
  required: 'required',
  requireTranslation: 'requireTranslation',
  atLeastOne: 'atLeastOne',
  cannotRemove: 'cannotRemove',
  userGroupMismatch: 'userGroupMismatch',
  pattern: 'pattern',
  incorrectValue: 'incorrectValue',
});

/**
 * Converts form validation error(s) to an UI error message for the user (i18n translation key + parameters).
 *
 * Only one validation error gets translated - rest is ignored (to not overwhelm the user).
 */
export function toErrorMessage(errors: ValidationErrors): { message: string; params: { [key: string]: any } } {
  if (!errors) return null;

  const key = Object.keys(errors)[0];
  switch (key) {
    case validationErrorCode.email:
      return { message: 'ERROR.EMAIL', params: {} };

    case validationErrorCode.max:
      return { message: 'ERROR.MAX_VALUE', params: { max: errors[key].max } };

    case validationErrorCode.maxLength:
      return {
        message: 'ERROR.MAX_LENGTH',
        params: {
          actualLength: errors[key].actualLength,
          requiredLength: errors[key].requiredLength,
        },
      };

    case validationErrorCode.min:
      return { message: 'ERROR.MIN_VALUE', params: { min: errors[key].min } };

    case validationErrorCode.minLength:
      return null;

    case validationErrorCode.required:
      return { message: 'ERROR.REQUIRED', params: {} };

    case validationErrorCode.pattern:
      return { message: 'ERROR.PATTERN', params: {} };

    case validationErrorCode.atLeastOne:
    case validationErrorCode.requireTranslation:
      const err: ValidationErrorAtLeastOne = errors[key];
      return { message: 'ERROR.REQUIRE_ONE_TRANSLATION', params: { names: err.translated ?? err.controls } };

    case validationErrorCode.cannotRemove:
      return { message: 'ERROR.CANNOT_BE_REMOVED', params: {} };

    default:
      // Accept both { message, params } objects and plain translation-key strings -
      // a string value would otherwise yield message: undefined, which makes the
      // errorMessage getters throw mid change detection and no error render at all.
      if (typeof errors[key] === 'string') return { message: errors[key], params: {} };
      return { message: errors[key].message, params: errors[key].params ?? {} };
  }
}

/**
 * Removes the specified error manually, and returns the remaining errors -OR- null if no errors remain.
 *
 * Calling removeError also updates the validity of the parent control (unless there were no errors to begin with).
 * @param errorCode
 * @returns The remaining errors -OR- null if no errors remain.
 */
export function removeFormError(form: AbstractControl, errorCode: string): ValidationErrors | null {
  const errors = form.errors;
  if (errors) {
    delete errors[errorCode];
    if (!Object.keys(errors).length) {
      form.setErrors(null);
    } else {
      form.setErrors(errors);
      return errors;
    }
  }
  return null;
}

export class FormControlEx<TValue = any> extends FormControl<TValue> {
  constructor(
    readonly caption: string,
    formState: any = null,
    validator?: ValidatorFn | ValidatorFn[] | null,
    asyncValidator?: AsyncValidatorFn | null,
    helpText?: string | HelpText,
    supplementalLabel?: string
  ) {
    super(formState, validator, asyncValidator);
    if (validator) {
      // Required-state is mutable through the `required` setter. Keep it out of
      // the stable validator list so turning required off cannot re-add it.
      this._validatorList = (Array.isArray(validator) ? validator : [validator]).filter(
        item => item !== Validators.required
      );
    }
    this.syncRequiredState();
    this.helpText = helpText;
    this.supplementalLabel = supplementalLabel;
  }

  private _required: boolean;
  private _isReadonly: boolean;
  private _validatorList: ValidatorFn[];
  maxLength: number;

  /** Omit this from form submits. */
  noSubmit: boolean;

  helpText: string | HelpText;
  supplementalLabel: string;
  readonly validatorsChanged: EventEmitter<ValidatorFn | ValidatorFn[] | null> = new EventEmitter();
  readonly touchStateChanged: EventEmitter<boolean> = new EventEmitter();
  readonly readonlyStateChanged: EventEmitter<boolean> = new EventEmitter();

  get isReadonly(): boolean {
    return this._isReadonly;
  }

  set isReadonly(value: boolean) {
    if (this._isReadonly === value) {
      return;
    }

    this._isReadonly = value;
    this.readonlyStateChanged.emit(value);
  }

  /**
   * Keep the Astral-required marker synchronized when callers add or remove an explicit required validator.
   */
  override setValidators(validators: ValidatorFn | ValidatorFn[] | null): void {
    super.setValidators(validators);
    this.syncRequiredState();
    this.validatorsChanged.emit(validators);
  }

  override addValidators(validators: ValidatorFn | ValidatorFn[]): void {
    super.addValidators(validators);
    this.syncRequiredState();
    this.validatorsChanged.emit(validators);
  }

  override clearValidators(): void {
    super.clearValidators();
    this.syncRequiredState();
    this.validatorsChanged.emit(null);
  }

  override removeValidators(validators: ValidatorFn | ValidatorFn[]): void {
    super.removeValidators(validators);
    this.syncRequiredState();
    this.validatorsChanged.emit(validators);
  }

  markAsTouched() {
    super.markAsTouched();
    this.touchStateChanged.emit(true);
  }

  markAsUntouched() {
    super.markAsUntouched();
    this.touchStateChanged.emit(false);
  }

  markAllAsTouched() {
    super.markAllAsTouched();
    this.touchStateChanged.emit(true);
  }

  /**
   * Prefer `required` for static field definitions and `setValidators` for conditional rules.
   */
  get required(): boolean {
    return this._required;
  }
  set required(value: boolean) {
    this._required = value;
    const validators: ValidatorFn[] | null = this._validatorList
      ? this._required
        ? [Validators.required, ...this._validatorList]
        : this._validatorList
      : this._required
        ? [Validators.required]
        : null;
    this.clearValidators();
    this.setValidators(validators);
    this.updateValueAndValidity();
  }

  getErrorMessage(): { message: string; params: any } {
    return toErrorMessage(this.errors);
  }

  private syncRequiredState(): void {
    this._required = this.hasValidator(Validators.required);
  }

  /**
   * Removes the specified error manually, and returns the remaining errors -OR- null if no errors remain.
   *
   * Calling removeError also updates the validity of the parent control (unless there were no errors to begin with).
   * @param errorCode
   * @returns The remaining errors -OR- null if no errors remain.
   */
  removeError(errorCode: string): ValidationErrors | null {
    return removeFormError(this, errorCode);
  }
}
